/**
 * The cash drawer, from the till it belongs to.
 *
 * Nothing opened one. The hooks existed and had no caller in either app, and
 * the server refuses cash without an open drawer — correctly, because cash with
 * no drawer is cash nobody can reconcile — so no till could take cash at all.
 *
 * **Counting comes before knowing.** At close the expected figure is not shown
 * until the count is in. The server returns it the whole shift, and showing it
 * would let whoever is counting count to the number: a £20 note that went home
 * in an apron becomes a miscount that happens to agree. The same rule as a
 * stocktake, for the same reason. During the shift the sheet shows the float
 * and when it opened, and nothing that adds up to what the drawer should hold.
 *
 * **A variance has a direction, in words.** Short and over are both exceptions
 * — a drawer consistently over is usually a tender being rung wrong — and
 * `describeDrawerVariance` in the client says which, so this screen and the day
 * report cannot word the same figure differently.
 *
 * **One drawer per till.** See `this-till.ts`. A site with no registered tills
 * gets one drawer for the whole site, and the sheet says so rather than
 * implying a precision it does not have.
 */

import { useState } from 'react';
import {
  displayLocale,
  describeDrawerVariance,
  formatMoney,
  useCloseDrawer,
  useCurrentDrawer,
  useOpenDrawer,
  usePermission,
  type Drawer,
} from '@restaurant/api-client';
import {
  ActionError,
  Button,
  ErrorNotice,
  Field,
  Input,
  Loading,
  NumberPad,
  Sheet,
} from '@restaurant/ui';

import { FLOATS } from './shift-status';
import { tillSettled, useThisTill } from './this-till';

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(displayLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Whether this till can take cash, in the header.
 *
 * Shown to anyone who takes payments, because a waiter who cannot open the
 * drawer still needs to know it is shut before telling a guest they can pay
 * cash.
 */
/** The one sheet a drawer is handled from. Each state replaces the last. */
export function DrawerSheet({
  locationId,
  onDismiss,
}: {
  locationId: string | undefined;
  onDismiss: () => void;
}) {
  const till = useThisTill(locationId);
  const drawer = useCurrentDrawer(
    tillSettled(till.state) ? locationId : undefined,
    till.terminalId,
  );
  const mayClose = usePermission('payments.drawer.close');
  const [counting, setCounting] = useState(false);
  const [closed, setClosed] = useState<Drawer | null>(null);

  const where =
    till.state.kind === 'chosen'
      ? till.state.till.name
      : till.state.kind === 'none-registered'
        ? 'this site'
        : 'this till';

  if (closed) return <ClosedSheet drawer={closed} where={where} onDismiss={onDismiss} />;

  if (till.state.kind === 'loading') {
    return (
      <Sheet title="Drawer" onDismiss={onDismiss}>
        <Loading label="Finding this till" />
      </Sheet>
    );
  }

  if (till.state.kind === 'unchosen') {
    return (
      <Sheet title="Which till is this?" onDismiss={onDismiss}>
        <p className="dr__note">
          Each till has its own drawer, so this tablet has to know which one it is before it can
          open one or take cash. It is asked once and remembered on this device.
        </p>
        <TillChoices locationId={locationId} />
      </Sheet>
    );
  }

  if (drawer.isPending) {
    return (
      <Sheet title="Drawer" onDismiss={onDismiss}>
        <Loading label="Checking the drawer" />
      </Sheet>
    );
  }
  if (drawer.isError) {
    return (
      <Sheet title="Drawer" onDismiss={onDismiss}>
        <ErrorNotice message={drawer.error.message} />
      </Sheet>
    );
  }

  if (drawer.data === null) {
    return (
      <OpenDrawerSheet
        locationId={locationId}
        terminalId={till.terminalId}
        where={where}
        onDismiss={onDismiss}
      />
    );
  }

  const live = drawer.data;

  if (counting) {
    return (
      <CountSheet
        drawer={live}
        where={where}
        onClosed={setClosed}
        onBack={() => setCounting(false)}
        onDismiss={onDismiss}
      />
    );
  }

  return (
    <Sheet
      title={`Drawer at ${where}`}
      onDismiss={onDismiss}
      actions={
        mayClose ? (
          <Button variant="primary" size="lg" block onClick={() => setCounting(true)}>
            Count and close
          </Button>
        ) : null
      }
    >
      <dl className="dr__facts">
        <dt>Open since</dt>
        <dd className="tabular">{timeOf(live.opened_at)}</dd>
        <dt>Float</dt>
        <dd className="tabular">{formatMoney(live.opening_float)}</dd>
      </dl>
      <p className="dr__note">
        What it should hold now is shown after it has been counted, not before — a count made
        while looking at the answer tends to agree with it.
      </p>
      {!mayClose && (
        <p className="dr__note">Closing the drawer is for a cashier or a manager.</p>
      )}
    </Sheet>
  );
}

function OpenDrawerSheet({
  locationId,
  terminalId,
  where,
  onDismiss,
}: {
  locationId: string | undefined;
  terminalId: string | undefined;
  where: string;
  onDismiss: () => void;
}) {
  const mayOpen = usePermission('payments.drawer.open');
  const open = useOpenDrawer();
  const [float, setFloat] = useState('');

  return (
    <Sheet
      title={`No drawer open at ${where}`}
      onDismiss={onDismiss}
      actions={
        mayOpen ? (
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!locationId}
            loading={open.isPending}
            onClick={() =>
              locationId &&
              open.mutate(
                {
                  location_id: locationId,
                  opening_float: float.trim() || '0',
                  ...(terminalId ? { terminal_id: terminalId } : {}),
                },
                { onSuccess: onDismiss },
              )
            }
          >
            Open with {formatMoney(float.trim() || '0')} float
          </Button>
        ) : null
      }
    >
      {mayOpen ? (
        <>
          <p className="dr__note">Count the float going in and enter it.</p>
          <div className="dr__presets">
            {FLOATS.map((preset) => (
              <button
                key={preset}
                type="button"
                className="pay__note tabular"
                onClick={() => setFloat(preset)}
              >
                {formatMoney(preset)}
              </button>
            ))}
          </div>
          <NumberPad label="Float" value={float} onChange={setFloat} />
        </>
      ) : (
        <p className="dr__note">
          Cash cannot be taken until a drawer is open, and opening one is for a cashier or a
          manager. Card, voucher and account payments still work.
        </p>
      )}

      <ActionError error={open.error} action="Opening the drawer" />
    </Sheet>
  );
}

function CountSheet({
  drawer,
  where,
  onClosed,
  onBack,
  onDismiss,
}: {
  drawer: Drawer;
  where: string;
  onClosed: (drawer: Drawer) => void;
  onBack: () => void;
  onDismiss: () => void;
}) {
  const close = useCloseDrawer();
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');

  return (
    <Sheet
      title={`Count the drawer at ${where}`}
      onDismiss={onDismiss}
      actions={
        <>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={counted.trim() === ''}
            loading={close.isPending}
            onClick={() =>
              close.mutate(
                {
                  drawerId: drawer.id,
                  counted_total: counted.trim(),
                  ...(note.trim() ? { note: note.trim() } : {}),
                },
                { onSuccess: onClosed },
              )
            }
          >
            {counted.trim() === ''
              ? 'Enter the count'
              : `Close with ${formatMoney(counted.trim())} counted`}
          </Button>
          <Button size="lg" block onClick={onBack}>
            Not yet
          </Button>
        </>
      }
    >
      {/* No expected figure on this sheet, on purpose. See the note at the
          top of the file. */}
      <p className="dr__note">
        Everything in the drawer, float included. What it should hold is shown once the count is
        in.
      </p>
      {/* The running figure is read back on the button — "Close with £107.00
          counted" — so a second readout above the pad was the same number
          twice, and the height it cost was the height the note needed. */}
      <div className="dr__count">
        <NumberPad label="Counted" value={counted} onChange={setCounted} />
        <Field
          label="Note"
          hint="Optional. Anything the next person should know about this count."
        >
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <ActionError error={close.error} action="Closing the drawer" />
    </Sheet>
  );
}

function ClosedSheet({
  drawer,
  where,
  onDismiss,
}: {
  drawer: Drawer;
  where: string;
  onDismiss: () => void;
}) {
  const variance = drawer.variance === null ? null : Number(drawer.variance);
  const tone = variance === null || variance === 0 ? 'ok' : 'off';

  return (
    <Sheet
      title={`Drawer at ${where} closed`}
      onDismiss={onDismiss}
      actions={
        <Button variant="primary" size="lg" block onClick={onDismiss}>
          Done
        </Button>
      }
    >
      <p className={`dr__verdict dr__verdict--${tone} tabular`}>
        {describeDrawerVariance(drawer.variance)}
      </p>
      <dl className="dr__facts">
        <dt>Counted</dt>
        <dd className="tabular">{formatMoney(drawer.counted_total ?? '0')}</dd>
        <dt>Should have held</dt>
        <dd className="tabular">{formatMoney(drawer.expected_total)}</dd>
      </dl>
      {/* How the expected figure was reached, so a short drawer can be argued
          with rather than just accepted. Tips are in the cash taken: a tip
          left in notes is physically in the drawer. */}
      <dl className="dr__facts dr__facts--quiet">
        <dt>Float</dt>
        <dd className="tabular">{formatMoney(drawer.opening_float)}</dd>
        <dt>Cash taken, with tips</dt>
        <dd className="tabular">+ {formatMoney(drawer.cash_in)}</dd>
        <dt>Refunded in cash</dt>
        <dd className="tabular">− {formatMoney(drawer.cash_out)}</dd>
      </dl>
      {variance !== null && variance !== 0 && (
        <p className="dr__note">
          Short and over are both worth a look. A drawer that is regularly over is usually a
          payment being rung as cash that was not.
        </p>
      )}
    </Sheet>
  );
}

/**
 * The registered tills at a site, to say which one this tablet is. Nothing at
 * all at a site with none registered: there is no choice to make, and a
 * paragraph saying so was the longest thing in the tablet menu.
 */
export function TillChoices({
  locationId,
  label,
}: {
  locationId: string | undefined;
  /** Said beside the choices, as a row of the tablet menu. */
  label?: string;
}) {
  const { state, choose } = useThisTill(locationId);

  if (state.kind === 'loading') return <Loading label="Loading the tills" />;
  if (state.kind === 'none-registered') return null;

  const current = state.kind === 'chosen' ? state.till.id : null;
  const choices = (
    <div className="dr__tills">
      {state.choices.map((till) => (
        <button
          key={till.id}
          type="button"
          className={`pos__site-btn${till.id === current ? ' is-active' : ''}`}
          aria-pressed={till.id === current}
          onClick={() => choose(till.id)}
        >
          <span className="pos__site-btn-name">{till.name}</span>
          {till.device_fingerprint && (
            <span className="pos__site-btn-code">{till.device_fingerprint}</span>
          )}
        </button>
      ))}
    </div>
  );
  if (!label) return choices;
  return (
    <div className="pos__till">
      <span className="pos__till-label">{label}</span>
      {choices}
    </div>
  );
}
